import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Row,
  Col,
  Divider,
  Typography,
  Skeleton,
  Avatar,
  theme,
  Tag,
  Button,
  Popconfirm,
  Switch,
  Tooltip,
  message,
} from 'antd';
import {
  CalendarOutlined,
  WarningOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  MessageOutlined,
  UserOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useIntl } from 'react-intl';
import { StoreContext } from '../store/index';
import { db } from '../firebase';
import { fetchUserById, removeFriend } from './api';
import { getOrCreateConversation } from '../chat/api';
import { fetchShiftsByUser, addFriend, notifyMatchParticipants } from '../shifts/api';
import { updateUserBlocked } from '../adminPanel/api';
import PlayerProfileHeader from '../components/PlayerProfileHeader';
import ReportModal from './ReportModal';

const StatCard = ({ label, value, suffix }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '8px 4px',
        flex: 1,
        minWidth: 0,
      }}>
      <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
        {value ?? 0}
        {suffix && (
          <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 2 }}>
            {suffix}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: token.colorTextSecondary, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
};

const PlayerProfileDrawer = ({ playerId, playerName, isGuest, invitedByName, invitedByPhotoUrl, visible, onClose }) => {
  const store = useContext(StoreContext);
  const {
    user: [user, , setUpdateUser],
    chat: [, openChat],
  } = store;
  const { token } = theme.useToken();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const [playerLoading, setPlayerLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const [computedStats, setComputedStats] = useState({
    games: 0, goals: 0, absent: 0, createdGames: 0, punctualityPct: null, absencePct: null,
  });
  const [recentStats, setRecentStats] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [blockSaving, setBlockSaving] = useState(false);

  const canBlock = user.isAdmin && playerId && playerId !== user.uid && !isGuest;

  const handleToggleBlocked = async next => {
    setBlockSaving(true);
    const prev = player?.blocked === true;
    setPlayer(p => (p ? { ...p, blocked: next } : p));
    try {
      await updateUserBlocked(playerId, next);
      message.success(next
        ? formatMessage({ id: 'admin.users.blocked.on' }, { name: player?.nickname || '' })
        : formatMessage({ id: 'admin.users.blocked.off' }, { name: player?.nickname || '' }));
    } catch {
      setPlayer(p => (p ? { ...p, blocked: prev } : p));
      message.error(formatMessage({ id: 'admin.users.blocked.error' }));
    } finally {
      setBlockSaving(false);
    }
  };

  useEffect(() => {
    fetchUserById(playerId, setPlayerLoading).then(data => setPlayer(data[0]));
    fetchShiftsByUser(playerId).then(shifts => {
      setComputedStats(prev => ({ ...prev, createdGames: shifts.length }));
    });
  }, [playerId]);

  useEffect(() => {
    if (!playerId) return undefined;

    const q = query(collection(db, 'stats'), where('userId', '==', playerId));
    const unsubscribe = onSnapshot(
      q,
      snapshot => {
        const stats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const games = new Set(stats.map(s => s.shiftId)).size;
        const goals = stats.reduce((acc, s) => acc + (s.goals ?? 0), 0);
        const absent = stats.filter(s => s.absent).length;
        const attended = stats.filter(s => !s.absent);
        const late = attended.filter(s => s.punctual === false).length;
        const punctualityPct = attended.length > 0
          ? Math.round(((attended.length - late) / attended.length) * 100)
          : null;
        const absencePct = stats.length > 0
          ? Math.round((absent / stats.length) * 100)
          : null;
        setComputedStats(prev => ({
          ...prev, games, goals, absent, punctualityPct, absencePct,
        }));

        const sorted = [...stats].sort((a, b) => {
          const da = a.date?.toDate?.() ?? new Date(0);
          const db2 = b.date?.toDate?.() ?? new Date(0);
          return db2 - da;
        });
        setRecentStats(sorted.slice(0, 3));
      },
      err => { if (err.code !== 'permission-denied') console.error('[Firestore] stats (drawer) listener:', err.code); },
    );

    return () => unsubscribe();
  }, [playerId]);

  const formatStatDate = date => {
    if (!date?.toDate) return '';
    return date.toDate().toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const { nickname, age, position, level, photoURL, email = '', tokens = 0, messageToken = '' } = player || {};
  const isAlreadyFriend = !!(email && user.friendsIds?.includes(email));
  const canAddFriend = !isGuest && player && user.uid !== playerId && email !== '' && !isAlreadyFriend;
  const canRemoveFriend = !isGuest && player && user.uid !== playerId && isAlreadyFriend;
  const canMessage = !isGuest && player && user.uid !== playerId;

  const handleOpenChat = () => {
    getOrCreateConversation(
      { uid: user.uid, nickname: user.nickname, photoURL: user.photoURL || '' },
      { uid: playerId, nickname: nickname || '', photoURL: photoURL || '' },
    ).then(convId => {
      openChat({
        conversationId: convId,
        recipientUid: playerId,
        recipientNickname: nickname || '',
        recipientPhotoURL: photoURL || '',
        recipientToken: messageToken || null,
      });
      onClose();
    }).catch(() => {});
  };

  const handleRemoveFriend = () => {
    if (!email) return;
    removeFriend(user.firestoreId, user.friendsIds ?? [], email, setRemoveLoading)
      .then(() => {
        setUpdateUser?.(true);
        message.success(formatMessage({ id: 'profile.remove.friend.done' }, { name: nickname || '' }));
      })
      .catch(err => message.error(err?.message || formatMessage({ id: 'profile.add.friend.error' })));
  };

  const handleAddFriend = () => {
    addFriend(user.firestoreId, user.friendsIds, email, setAddLoading)
      .then(() => {
        notifyMatchParticipants({
          shiftId: '',
          senderUserId: user.uid,
          recipientUserIds: [playerId],
          type: 'general',
          title: formatMessage({ id: 'friend.added.title' }),
          body: formatMessage({ id: 'friend.added.body' }, { name: user.nickname }),
          url: 'https://www.gamebooking.top/players',
          from: user.nickname,
          actorPhoto: user.photoURL ?? '',
          action: formatMessage({ id: 'notif.action.friend' }),
          archive: true,
        });
      })
      .catch(() => message.error(formatMessage({ id: 'profile.add.friend.error' })));
  };

  return (
    <>
      <Drawer
        title={formatMessage({ id: 'profile.drawer.title' })}
        placement="right"
        onClose={onClose}
        extra={
          <Row gutter={[6, 6]} wrap justify="end">
            {canMessage && (
              <Col>
                <Tooltip title={formatMessage({ id: 'chat.open' })}>
                  <Button
                    size="small"
                    icon={<MessageOutlined />}
                    onClick={handleOpenChat}
                  />
                </Tooltip>
              </Col>
            )}
            {canAddFriend && (
              <Col>
                <Popconfirm
                  title={formatMessage({ id: 'profile.add.friend' })}
                  description={formatMessage({ id: 'profile.add.friend.confirm' })}
                  onConfirm={handleAddFriend}
                  okText={formatMessage({ id: 'common.yes' })}
                  cancelText={formatMessage({ id: 'common.no' })}>
                  <Button size="small" type="primary" loading={addLoading} icon={<UserAddOutlined />} />
                </Popconfirm>
              </Col>
            )}
            {canRemoveFriend && (
              <Col>
                <Popconfirm
                  title={formatMessage({ id: 'profile.remove.friend' })}
                  description={formatMessage({ id: 'profile.remove.friend.confirm' }, { name: nickname || '' })}
                  onConfirm={handleRemoveFriend}
                  okText={formatMessage({ id: 'common.yes' })}
                  cancelText={formatMessage({ id: 'common.no' })}>
                  <Button size="small" danger ghost loading={removeLoading} icon={<UserDeleteOutlined />} />
                </Popconfirm>
              </Col>
            )}
            <Col>
              <Tooltip title={formatMessage({ id: 'profile.report' })}>
                <Button
                  danger
                  ghost
                  size="small"
                  icon={<WarningOutlined />}
                  onClick={() => setShowReportModal(true)}
                />
              </Tooltip>
            </Col>
          </Row>
        }
        open={visible}>
        {isGuest && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 32, gap: 12 }}>
            {/* Composed avatar: inviter photo + guest badge */}
            <div style={{ position: 'relative', width: 80, height: 80 }}>
              <Avatar
                size={80}
                src={invitedByPhotoUrl || null}
                icon={<UserOutlined />}
              />
              <Avatar
                size={36}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  border: `2px solid ${token.colorBgContainer}`,
                  background: token.colorFillSecondary,
                  color: token.colorTextSecondary,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                ?
              </Avatar>
            </div>
            <Typography.Title level={4} style={{ margin: 0 }}>{playerName}</Typography.Title>
            <Tag color="default">{formatMessage({ id: 'player.guest.label' })}</Tag>
            {invitedByName && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar size={20} src={invitedByPhotoUrl || null} icon={<UserOutlined />} />
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {formatMessage({ id: 'player.guest.invited.by' }, { name: invitedByName })}
                </Typography.Text>
              </div>
            )}
            <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center', paddingTop: 4 }}>
              {formatMessage({ id: 'player.guest.description' })}
            </Typography.Text>
          </div>
        )}
        {!isGuest && (playerLoading || !player) && (
          <Skeleton active avatar={{ size: 80 }} paragraph={{ rows: 4 }} />
        )}
        {!isGuest && !playerLoading && player && (
          <>
            {/* Header — same gradient card as the profile page */}
            <PlayerProfileHeader
              photoURL={photoURL}
              nickname={nickname}
              age={age}
              position={position}
              level={level}
              email={user.isAdmin || playerId === user.uid ? email : undefined}
              phone={
                // eslint-disable-next-line no-nested-ternary
                playerId === user.uid
                  ? user.phoneNumber || player.phoneNumber
                  : user.isAdmin
                  ? player.phoneNumber
                  : undefined
              }
            />

            {/* Stats — 3-per-row grid so 6-7 tiles wrap cleanly on mobile
                instead of squeezing into one horizontal strip that clipped
                labels ("Jugados" bleeding into "Goles"). */}
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'player.props.games' })}
                  value={computedStats.games}
                />
              </Col>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'player.props.goals' })}
                  value={computedStats.goals}
                />
              </Col>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'profile.stats.organized' })}
                  value={computedStats.createdGames}
                />
              </Col>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'player.props.absent' })}
                  value={computedStats.absent}
                />
              </Col>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'profile.stats.punctuality' })}
                  value={computedStats.punctualityPct == null ? '—' : computedStats.punctualityPct}
                  suffix={computedStats.punctualityPct == null ? '' : '%'}
                />
              </Col>
              <Col xs={8}>
                <StatCard
                  label={formatMessage({ id: 'profile.stats.absencePct' })}
                  value={computedStats.absencePct == null ? '—' : computedStats.absencePct}
                  suffix={computedStats.absencePct == null ? '' : '%'}
                />
              </Col>
              {(user.isAdmin || user.uid === playerId) && (
                <Col xs={8}>
                  <StatCard
                    label={formatMessage({ id: 'profile.stats.tokens' })}
                    value={tokens}
                    suffix="€"
                  />
                </Col>
              )}
            </Row>

            {/* Recent matches */}
            {recentStats.length > 0 && (
              <>
                <Divider style={{ margin: '8px 0 12px' }} />
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {formatMessage({ id: 'profile.stats.recent' })}
                </Typography.Text>
                <div style={{ marginTop: 8 }}>
                  {recentStats.map(s => (
                    <Row
                      key={`${s.shiftId}-${s.date?.seconds}`}
                      justify="space-between"
                      align="middle"
                      onClick={() => navigate(`/shifts?${s.shiftId}`)}
                      style={{
                        padding: '8px 10px',
                        marginBottom: 4,
                        background: token.colorBgElevated,
                        borderRadius: token.borderRadius,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        cursor: 'pointer',
                      }}>
                      <Col>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {formatStatDate(s.date)}
                        </Typography.Text>
                      </Col>
                      <Col>
                        <Row gutter={6}>
                          {s.goals > 0 && (
                            <Col>
                              <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                                {`⚽ ×${s.goals}`}
                              </Tag>
                            </Col>
                          )}
                          {s.yellowCard > 0 && (
                            <Col>
                              <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>
                                🟨
                              </Tag>
                            </Col>
                          )}
                          {s.redCard && (
                            <Col>
                              <Tag color="red" style={{ margin: 0, fontSize: 11 }}>
                                🟥
                              </Tag>
                            </Col>
                          )}
                          {s.absent && (
                            <Col>
                              <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                                {formatMessage({ id: 'player.props.absent' })}
                              </Tag>
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  ))}
                </div>
              </>
            )}

            {canBlock && (
              <>
                <Divider style={{ margin: '16px 0 12px' }} />
                <div
                  style={{
                    background: token.colorFillQuaternary,
                    borderRadius: token.borderRadiusLG,
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <LockOutlined style={{ color: token.colorError }} />
                    <div style={{ minWidth: 0 }}>
                      <Typography.Text strong style={{ display: 'block' }}>
                        {formatMessage({ id: 'admin.users.blocked.label' })}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {formatMessage({ id: 'admin.users.blocked.hint' })}
                      </Typography.Text>
                    </div>
                  </div>
                  <Switch
                    checked={player?.blocked === true}
                    loading={blockSaving}
                    onChange={handleToggleBlocked}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Drawer>

      {showReportModal && (
        <ReportModal
          visible={showReportModal}
          onClose={() => setShowReportModal(false)}
          reportedUserId={playerId}
          reportedNickname={nickname}
        />
      )}
    </>
  );
};

export default PlayerProfileDrawer;
